import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../config';
import authService from '../utils/authService';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('🚀 Starting login process...');
      console.log('📝 Form data:', formData);
      
      const requestBody = JSON.stringify(formData);
      console.log('📤 Request body:', requestBody);
      
      const response = await fetch(`${config.API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // This is crucial for session management
        body: requestBody
      }).catch(fetchError => {
        console.error('🌐 Network/Fetch Error:', fetchError);
        throw new Error('Network connection failed. Please check your internet connection.');
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);
      
      // Handle different status codes explicitly
      if (response.status === 401) {
        console.log('🔐 Received 401 Unauthorized - parsing error response');
        
        try {
          const errorData = await response.json();
          console.log('📥 401 Error data:', errorData);
          
          // Handle specific error types from 401 response
          switch (errorData.errorType) {
            case 'not_approved':
              console.log('🚫 User not approved');
              toast.error('🚫 Account Not Approved: Your account is pending admin approval. Please wait for approval or contact support.');
              return;
            case 'inactive':
              console.log('⏸️ User account inactive');
              toast.error('⏸️ Account Deactivated: Your account is temporarily deactivated. Please contact support to reactivate your account.');
              return;
            case 'invalid_credentials':
              console.log('🔐 Invalid credentials');
              toast.error('🔐 Invalid Credentials: Please check your email and password and try again.');
              return;
            default:
              console.log('❓ Unknown 401 error');
              toast.error(errorData.message || 'Authentication failed. Please try again.');
              return;
          }
        } catch (parseError) {
          console.error('❌ Failed to parse 401 response:', parseError);
          toast.error('Authentication failed. Please try again.');
          return;
        }
      }
      
      // Handle successful responses (200)
      if (response.status === 200) {
        console.log('✅ Received 200 OK - parsing success response');
        
        try {
          const successData = await response.json();
          console.log('📥 Success data:', successData);
          
          if (successData.success) {
            console.log('🎉 Login successful!');
            
            // Store JWT token and user data
            if (successData.token) {
              authService.setToken(successData.token);
              authService.setUser(successData.user);
              console.log('🔐 JWT Token stored successfully');
            }
            
            toast.success(`Welcome back, ${successData.user?.firstName || 'User'}!`);
            
            // Handle redirect after login
            const returnUrl = searchParams.get('returnUrl');
            console.log('🔍 Login redirect debug:');
            console.log('- returnUrl:', returnUrl);
            console.log('- user role:', successData.user?.role);
            console.log('- user data:', successData.user);
            
            // Normalize role format (handle both "AGENT" and "ROLE_AGENT" formats)
            const userRole = successData.user?.role;
            const normalizedRole = userRole?.startsWith('ROLE_') ? userRole : `ROLE_${userRole}`;
            console.log('🔄 Normalized role:', normalizedRole);
            
            // Add a small delay to ensure token is properly stored
            setTimeout(() => {
              if (returnUrl) {
                console.log('🎯 Redirecting to returnUrl:', returnUrl);
                navigate(returnUrl, { replace: true });
              } else {
                // Default redirect based on user role
                if (normalizedRole === 'ROLE_AGENT') {
                  console.log('🎯 Redirecting agent to /agent/dashboard');
                  navigate('/agent/dashboard', { replace: true });
                } else if (normalizedRole === 'ROLE_SUPPORT') {
                  console.log('🎯 Redirecting support to /support/dashboard');
                  navigate('/support/dashboard', { replace: true });
                } else if (normalizedRole === 'ROLE_ADMIN') {
                  console.log('🎯 Redirecting admin to /admin/dashboard');
                  navigate('/admin/dashboard', { replace: true });
                } else {
                  console.log('🎯 Redirecting to generic dashboard');
                  navigate('/dashboard', { replace: true });
                }
              }
            }, 200); // Small delay to ensure proper state updates
            return;
          } else {
            console.log('❌ Success response but success=false');
            toast.error(successData.message || 'Login failed. Please try again.');
            return;
          }
        } catch (parseError) {
          console.error('❌ Failed to parse 200 response:', parseError);
          toast.error('Server response error. Please try again.');
          return;
        }
      }
      
      // Handle other status codes
      console.log(`❓ Unexpected status code: ${response.status}`);
      const responseText = await response.text();
      console.log('📥 Unexpected response:', responseText);
      toast.error(`Server error (${response.status}). Please try again.`);
      
    } catch (error) {
      console.error('💥 Catch block - Unexpected error:', error);
      console.error('💥 Error type:', typeof error);
      console.error('💥 Error name:', error.name);
      console.error('💥 Error message:', error.message);
      
      // Handle network errors
      if (error.message.includes('Network connection failed')) {
        toast.error(error.message);
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('Cannot connect to server. Please check if the backend is running.');
      } else {
        toast.error(`Unexpected error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoToRegister = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Filter Hotels</h1>
          <p className="text-gray-600">Your gateway to luxury accommodations</p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
            <p className="text-gray-600">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-gray-400 hover:text-gray-600`}></i>
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                onClick={handleGoToRegister}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign up here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;